/**
 * useForecastAccuracy composable
 *
 * Manages forecast accuracy analysis state: definition config, locations, 
 * bulk job submission via BulkTaskJobPackage, polling, and client-side score re-evaluation.
 *
 * Pattern follows FlexibleWeatherTracker.vue lifecycle:
 *   save → poll (delayed retry) → loadResults (fetch URL) → display
 *   load saved definitions → selectDefinition → restore state → optionally load results
 */

import { ref, reactive, computed } from 'vue';
import { getServerRoot } from '../weatherexceptions/apiConfig.js';
import { usePackageSave } from '../queryapp/usePackageSave.js';
import { VcJobStatus } from '@/visualcrossing-core/vcjobstatus';
// Ensure vcCommunicator bulk/metadata methods are loaded
import '/src/visualcrossing-core/vccommunicator_bulkdata';
import '/src/visualcrossing-core/vccommunicator_metadata';

export function useForecastAccuracy(options = {}) {
  const {
    storageKey = 'forecast-accuracy',
    serverRoot: serverRootProp = null
  } = options;

  // =========================================================================
  // State
  // =========================================================================

  // Session
  const sessionId = ref(null);
  const sessionState = ref(null);
  const isSessionReady = computed(() => !!sessionId.value && !!sessionState.value);
  const serverRoot = ref(serverRootProp);

  // Definition config
  const version = ref('v2');            // v1, v2, v2ext
  const maxLeadDays = ref(5);
  const unitGroup = ref('us');
  const startDate = ref('');            // YYYY-MM-DD
  const endDate = ref('');              // YYYY-MM-DD

  const dailyElements = ref([
    'tempmax', 'tempmin', 'precip', 'snow',
    'cloudcover', 'humidity', 'windspeed', 'pressure', 'solarenergy'
  ]);
  const hourlyElements = ref([
    'temp', 'humidity', 'windspeed', 'pressure', 'cloudcover', 'solarradiation'
  ]);

  const scoringConfig = reactive({
    tempThreshold: 3.0,
    windThreshold: 5.0,
    precipRainThreshold: 0.01,
    weightHighTemp: 0.30,
    weightLowTemp: 0.30,
    weightPrecip: 0.30,
    weightWind: 0.10
  });

  // Locations
  const locations = ref([]);

  // Metadata — links to the saved server package
  const localMetadata = ref(null);
  const taskInstance = ref(null);

  // Polling state (matches FlexibleWeatherTracker pattern)
  const pollingState = reactive({
    isRefreshingResults: false,
    isPolling: false,
    pollingRetries: 0,
    hasExceedRetries: false
  });
  let pollingTimer = null;

  // Save modal state
  const isSaving = ref(false);
  const saveError = ref(null);
  const saveDefnName = ref('');
  const saveDefnDesc = ref('');
  const pendingRunOnce = ref(true);
  const showConfirmOverwrite = ref(false);
  const overwriteResolve = ref(null);

  // Load definitions state
  const savedDefinitions = ref([]);
  const isLoadingDefinitions = ref(false);

  // Results
  const isLoadingResult = ref(false);
  const results = ref(null);
  const clientScores = ref(null);
  const runError = ref(null);

  // Callback for poll completion (set by Vue component to auto-close modal)
  let onPollComplete = null;

  // =========================================================================
  // Computed
  // =========================================================================

  const canRun = computed(() => {
    return isSessionReady.value &&
      locations.value.length > 0 &&
      startDate.value && endDate.value;
  });

  const canSave = computed(() => {
    return isSessionReady.value &&
      locations.value.length > 0 &&
      startDate.value && endDate.value;
  });

  const hasResults = computed(() => results.value !== null);

  const hasResultError = computed(() =>
    taskInstance.value && VcJobStatus.isError(taskInstance.value.status)
  );

  const hasResultComplete = computed(() =>
    taskInstance.value && VcJobStatus.isComplete(taskInstance.value.status)
  );

  const summary = computed(() => results.value?.summary || null);

  const locationResults = computed(() => results.value?.locations || []);

  const definition = computed(() => results.value?.definition || null);

  const pollingStatus = computed(() => {
    if (!pollingState.isRefreshingResults) return '';
    if (!taskInstance.value) return 'Waiting for job to start...';
    const inst = taskInstance.value;
    if (VcJobStatus.isExecuting(inst.status)) {
      if (!inst.completed) return 'Running...';
      return `Running... ${inst.completed}/${inst.total || '?'}`;
    }
    return 'Checking status...';
  });

  const isRunning = computed(() => pollingState.isRefreshingResults);

  // Build the definition JSON to send to the backend
  const definitionJson = computed(() => {
    return {
      version: version.value,
      maxLeadDays: maxLeadDays.value,
      dailyElements: dailyElements.value,
      hourlyElements: hourlyElements.value,
      unitGroup: unitGroup.value,
      scoringConfig: { ...scoringConfig }
    };
  });

  // =========================================================================
  // Session
  // =========================================================================

  function initSession() {
    const vc = window.vcCommunicator;
    if (!vc) return;

    sessionId.value = vc.sessionId;
    sessionState.value = vc.sessionState;

    vc.addHandler('createsession', (sid, state) => {
      sessionId.value = sid;
      sessionState.value = state;
      // Try to restore persisted metadata on login
      restorePersistedMetadata();
    });

    vc.addHandler('terminatesession', () => {
      sessionId.value = null;
      sessionState.value = null;
    });

    // If already logged in, try restore
    if (sessionId.value) {
      restorePersistedMetadata();
    }
  }

  // =========================================================================
  // Locations
  // =========================================================================

  function setLocations(locs) {
    locations.value = locs.map(l => ({
      location: l.location || l,
      name: l.name || l.location || l
    }));
  }

  function addLocation(location, name) {
    if (!location) return;
    locations.value.push({ location, name: name || location });
  }

  function removeLocation(index) {
    locations.value.splice(index, 1);
  }

  function clearLocations() {
    locations.value = [];
  }

  // =========================================================================
  // Save & Run (follows FlexibleWeatherTracker saveAsBulkTask pattern)
  // =========================================================================

  const { savePackage: savePackageCore } = usePackageSave();

  function buildLocalPackage(runOnce) {
    return {
      params: {
        goal: 'forecastaccuracy',
        startdate: startDate.value,
        enddate: endDate.value,
        unitGroup: unitGroup.value,
        contentType: 'json',
        forecastaccuracydefinition: JSON.stringify(definitionJson.value)
      },
      locations: locations.value.map(l => ({
        location: l.location,
        name: l.name
      })),
      runOnce: runOnce,
      enabled: true,
      type: 4,
      accountUserId: window.vcCommunicator?.sessionState?.userMdId || null
    };
  }

  async function saveAndRun() {
    pendingRunOnce.value = true;
    await saveAsBulkTask();
  }

  async function saveOnly() {
    pendingRunOnce.value = false;
    await saveAsBulkTask();
  }

  async function saveAsBulkTask() {
    const name = saveDefnName.value.trim();
    if (!name) return;

    isSaving.value = true;
    saveError.value = null;
    runError.value = null;
    results.value = null;
    clientScores.value = null;

    try {
      const localPackage = buildLocalPackage(pendingRunOnce.value);

      const metadata = {
        id: localMetadata.value?.id || undefined,
        name: name,
        desc: saveDefnDesc.value.trim(),
        type: 'JobPackage'
      };

      const sState = window.vcCommunicator?.sessionState || { userMdId: null };
      const result = await savePackageCore(localPackage, metadata, sState, {
        onConfirmOverwrite: async (existingPackage, packageName) => {
          return new Promise((resolve) => {
            showConfirmOverwrite.value = true;
            overwriteResolve.value = resolve;
          });
        }
      });

      if (result.success) {
        // Extract metadata — server may return as .metadata or .jobPackage
        const meta = result.metadata || result.result?.jobPackage || result.result?.metadata;
        localMetadata.value = meta;
        persistMetadata();

        if (pendingRunOnce.value) {
          startPollForCompletion();
        }
      } else if (result.code !== 'USER_CANCELLED') {
        saveError.value = result.error || 'Failed to save definition.';
      }
    } catch (err) {
      console.error('Failed to save as bulk task:', err);
      saveError.value = 'An unexpected error occurred while saving.';
    } finally {
      isSaving.value = false;
    }
  }

  function confirmOverwrite() {
    if (overwriteResolve.value) {
      overwriteResolve.value(true);
      overwriteResolve.value = null;
    }
    showConfirmOverwrite.value = false;
  }

  function cancelOverwrite() {
    if (overwriteResolve.value) {
      overwriteResolve.value(false);
      overwriteResolve.value = null;
    }
    showConfirmOverwrite.value = false;
  }

  // =========================================================================
  // Polling (matches FlexibleWeatherTracker pattern exactly)
  // =========================================================================

  function startPollForCompletion() {
    pollingState.isRefreshingResults = true;
    pollingState.pollingRetries = 0;
    pollingState.hasExceedRetries = false;
    taskInstance.value = null;
    delayedRefreshPackageResults();
  }

  function delayedRefreshPackageResults(isError) {
    if (pollingTimer) clearTimeout(pollingTimer);
    pollingTimer = setTimeout(() => {
      refreshPackageResults();
    }, isError ? 10000 : 5000);
  }

  function refreshPackageResults() {
    pollingState.pollingRetries++;
    if (pollingState.pollingRetries > 60) {
      pollingState.hasExceedRetries = true;
      pollingState.isRefreshingResults = false;
      runError.value = 'Job timed out. Check the job status in your account.';
      return;
    }
    if (pollingState.isPolling) return;
    if (!localMetadata.value?.id) return;

    pollingState.isPolling = true;
    window.vcCommunicator.resultsByJobPackage(localMetadata.value.id)
      .then((packageResults) => {
        if (packageResults && packageResults[0] && packageResults[0].instance) {
          taskInstance.value = packageResults[0].instance;
          if (packageResults[0].runonce) {
            taskInstance.value.status = VcJobStatus.STATUS.QUEUED;
          }
        } else {
          taskInstance.value = null;
        }
        pollingState.isRefreshingResults =
          (!taskInstance.value || !VcJobStatus.isFinishedState(taskInstance.value.status));

        if (pollingState.isRefreshingResults) {
          delayedRefreshPackageResults();
        } else if (VcJobStatus.isComplete(taskInstance.value.status)) {
          loadResults().then(() => {
            // Notify listeners (Vue component will close modal)
            if (onPollComplete) onPollComplete('complete');
          });
        } else if (VcJobStatus.isError(taskInstance.value.status)) {
          runError.value = taskInstance.value.info || 'Job failed';
          if (onPollComplete) onPollComplete('error');
        }
      })
      .catch((error) => {
        console.warn('Poll error:', error);
        pollingState.isRefreshingResults =
          pollingState.isRefreshingResults &&
          (!taskInstance.value || !VcJobStatus.isFinishedState(taskInstance.value.status));
        if (pollingState.isRefreshingResults) {
          delayedRefreshPackageResults(true);
        }
      })
      .finally(() => {
        pollingState.isPolling = false;
      });
  }

  function stopPolling() {
    if (pollingTimer) {
      clearTimeout(pollingTimer);
      pollingTimer = null;
    }
    pollingState.isRefreshingResults = false;
    pollingState.isPolling = false;
  }

  // =========================================================================
  // Load Results (follows FlexibleWeatherTracker.loadResultIntoDisplay)
  // =========================================================================

  async function loadResults() {
    const vc = window.vcCommunicator;
    if (!vc || !localMetadata.value) return false;

    isLoadingResult.value = true;
    try {
      const url = vc.constructBulkWeatherDataRequest(localMetadata.value, true, false);
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();

      if (data && typeof data === 'object') {
        results.value = data;
        reEvaluateScores();
        return true;
      }
      return false;
    } catch (e) {
      console.error('Error loading results:', e);
      runError.value = 'Failed to load results: ' + e.message;
      return false;
    } finally {
      isLoadingResult.value = false;
    }
  }

  // =========================================================================
  // Load / Manage Saved Definitions
  // =========================================================================

  async function loadSavedDefinitions() {
    const vc = window.vcCommunicator;
    if (!vc) return;

    isLoadingDefinitions.value = true;
    savedDefinitions.value = [];
    try {
      const results = await vc.resultsByJobPackage(null, 'forecastaccuracy');
      const allPackages = (results || []).filter(r => r?.jobPackage).map(r => {
        const pkg = r.jobPackage;
        if (r.instance) pkg.instance = r.instance;
        if (r.results) pkg.results = r.results;
        return pkg;
      });

      // Filter by goal tag
      const filtered = [];
      for (const pkg of allPackages) {
        if (pkg.tags && Array.isArray(pkg.tags)) {
          if (pkg.tags.some(t => t === 'Goal: forecastaccuracy')) {
            filtered.push(pkg);
            continue;
          }
          if (pkg.tags.length > 0) continue;
        }
        // No tags — fall back to loading definition
        try {
          const defn = await vc.loadBulkSourceDefn(pkg.id);
          if (defn?.params?.goal === 'forecastaccuracy') {
            filtered.push(pkg);
          }
        } catch (e) { /* skip */ }
      }
      savedDefinitions.value = filtered;
    } catch (err) {
      console.error('Failed to load definitions:', err);
    } finally {
      isLoadingDefinitions.value = false;
    }
  }

  async function selectDefinition(metadata) {
    const vc = window.vcCommunicator;
    if (!vc) return;

    try {
      const taskDefn = await vc.loadBulkSourceDefn(metadata.id);
      onLoadDefinition(metadata, taskDefn);
    } catch (err) {
      console.error('Failed to load definition:', err);
    }
  }

  function onLoadDefinition(metadata, packageDefn) {
    try {
      const pkg = packageDefn || {};

      // Restore locations
      if (pkg.locations?.length > 0) {
        setLocations(pkg.locations);
      }

      // Restore params
      if (pkg.params?.unitGroup) unitGroup.value = pkg.params.unitGroup;
      if (pkg.params?.startdate) startDate.value = pkg.params.startdate;
      if (pkg.params?.enddate) endDate.value = pkg.params.enddate;

      // Restore forecast accuracy definition
      if (pkg.params?.forecastaccuracydefinition) {
        try {
          const defn = JSON.parse(pkg.params.forecastaccuracydefinition);
          if (defn.version) version.value = defn.version;
          if (defn.maxLeadDays != null) maxLeadDays.value = defn.maxLeadDays;
          if (defn.dailyElements) dailyElements.value = defn.dailyElements;
          if (defn.hourlyElements) hourlyElements.value = defn.hourlyElements;
          if (defn.scoringConfig) Object.assign(scoringConfig, defn.scoringConfig);
        } catch (e) {
          console.error('Failed to parse forecastaccuracydefinition:', e);
        }
      }

      // Restore metadata linkage
      localMetadata.value = metadata || null;
      if (metadata?.name) saveDefnName.value = metadata.name;
      if (metadata?.desc) saveDefnDesc.value = metadata.desc;
      persistMetadata();

      // If completed results exist, load them directly
      if (metadata?.instance && metadata.instance.status === 3) {
        taskInstance.value = metadata.instance;
        pollingState.isRefreshingResults = false;
        pollingState.isPolling = false;
        loadResults();
      } else {
        results.value = null;
        clientScores.value = null;
        taskInstance.value = null;
      }
    } catch (err) {
      console.error('Failed to restore definition:', err);
    }
  }

  async function deleteDefinition(defn) {
    const vc = window.vcCommunicator;
    if (!vc) return;

    try {
      await vc.deleteMetadata(defn);
      savedDefinitions.value = savedDefinitions.value.filter(d => d.id !== defn.id);
      if (localMetadata.value?.id === defn.id) {
        newDefinition();
      }
    } catch (err) {
      console.error('Failed to delete definition:', err);
    }
  }

  function newDefinition() {
    localMetadata.value = null;
    taskInstance.value = null;
    results.value = null;
    clientScores.value = null;
    saveDefnName.value = '';
    saveDefnDesc.value = '';
    runError.value = null;
    persistMetadata();
  }

  // =========================================================================
  // Download helpers
  // =========================================================================

  function downloadResultJson() {
    const vc = window.vcCommunicator;
    if (!vc || !localMetadata.value) return;
    const url = vc.constructBulkWeatherDataRequest(localMetadata.value, true, false);
    window.open(url);
  }

  function downloadResultCsv() {
    const vc = window.vcCommunicator;
    if (!vc || !localMetadata.value) return;
    const url = vc.constructFormattedDataRequest(localMetadata.value, true, 'csv');
    window.open(url);
  }

  // =========================================================================
  // Metadata persistence (localStorage) — matches FlexibleWeatherTracker
  // =========================================================================

  function persistMetadata() {
    try {
      if (localMetadata.value) {
        localStorage.setItem(storageKey + '_metadata', JSON.stringify({
          id: localMetadata.value.id,
          name: localMetadata.value.name,
          desc: localMetadata.value.desc
        }));
      } else {
        localStorage.removeItem(storageKey + '_metadata');
      }
    } catch (e) { /* ignore */ }
  }

  function restorePersistedMetadata() {
    try {
      const saved = localStorage.getItem(storageKey + '_metadata');
      if (!saved) return;
      const meta = JSON.parse(saved);
      if (meta?.id) {
        // Load the full definition from server
        selectDefinition(meta);
      }
    } catch (e) { /* ignore */ }
  }

  // =========================================================================
  // Client-side score re-evaluation
  // =========================================================================

  /**
   * Re-evaluate ForecastWatch scores using current scoringConfig thresholds.
   * Works on the raw comparison data returned by the backend.
   */
  function reEvaluateScores() {
    if (!results.value?.locations) return;

    const tempThresh = scoringConfig.tempThreshold;
    const windThresh = scoringConfig.windThreshold;
    const precipThresh = scoringConfig.precipRainThreshold;

    const allLocationScores = [];

    for (const loc of results.value.locations) {
      if (!loc.comparisons) continue;

      // Per lead-day accumulators
      const leadDayBuckets = {};

      for (const comp of loc.comparisons) {
        const ld = comp.leadDay;
        if (!leadDayBuckets[ld]) {
          leadDayBuckets[ld] = {
            highTempPass: 0, highTempTotal: 0,
            lowTempPass: 0, lowTempTotal: 0,
            precipPass: 0, precipTotal: 0,
            windPass: 0, windTotal: 0
          };
        }
        const bucket = leadDayBuckets[ld];

        // High temp
        const tempmax = comp.daily?.tempmax;
        if (tempmax && isFinite(tempmax.error)) {
          bucket.highTempTotal++;
          if (Math.abs(tempmax.error) <= tempThresh) bucket.highTempPass++;
        }

        // Low temp
        const tempmin = comp.daily?.tempmin;
        if (tempmin && isFinite(tempmin.error)) {
          bucket.lowTempTotal++;
          if (Math.abs(tempmin.error) <= tempThresh) bucket.lowTempPass++;
        }

        // Precip (binary: did it rain?)
        const precip = comp.daily?.precip;
        if (precip && isFinite(precip.observed) && isFinite(precip.forecast)) {
          bucket.precipTotal++;
          const obsRain = precip.observed > precipThresh;
          const fcstRain = precip.forecast > precipThresh;
          if (obsRain === fcstRain) bucket.precipPass++;
        }

        // Wind
        const wind = comp.daily?.windspeed;
        if (wind && isFinite(wind.error)) {
          bucket.windTotal++;
          if (Math.abs(wind.error) <= windThresh) bucket.windPass++;
        }
      }

      // Compute percentages
      const locScores = {};
      for (const [ld, bucket] of Object.entries(leadDayBuckets)) {
        const highTempAcc = bucket.highTempTotal > 0 ? 100 * bucket.highTempPass / bucket.highTempTotal : null;
        const lowTempAcc = bucket.lowTempTotal > 0 ? 100 * bucket.lowTempPass / bucket.lowTempTotal : null;
        const precipAcc = bucket.precipTotal > 0 ? 100 * bucket.precipPass / bucket.precipTotal : null;
        const windAcc = bucket.windTotal > 0 ? 100 * bucket.windPass / bucket.windTotal : null;
        const composite = computeComposite(highTempAcc, lowTempAcc, precipAcc, windAcc);
        locScores[ld] = { highTempAcc, lowTempAcc, precipAcc, windAcc, composite };
      }

      allLocationScores.push({ name: loc.name, leadDayScores: locScores });
    }

    // Global aggregation
    const globalBuckets = {};
    for (const loc of allLocationScores) {
      for (const [ld, scores] of Object.entries(loc.leadDayScores)) {
        if (!globalBuckets[ld]) {
          globalBuckets[ld] = { values: [] };
        }
        globalBuckets[ld].values.push(scores);
      }
    }
    const globalScores = {};
    for (const [ld, bucket] of Object.entries(globalBuckets)) {
      const avg = (arr, key) => {
        const valid = arr.map(v => v[key]).filter(v => v !== null);
        return valid.length > 0 ? valid.reduce((a, b) => a + b, 0) / valid.length : null;
      };
      globalScores[ld] = {
        highTempAcc: avg(bucket.values, 'highTempAcc'),
        lowTempAcc: avg(bucket.values, 'lowTempAcc'),
        precipAcc: avg(bucket.values, 'precipAcc'),
        windAcc: avg(bucket.values, 'windAcc'),
        composite: avg(bucket.values, 'composite')
      };
    }

    clientScores.value = {
      locations: allLocationScores,
      global: globalScores,
      config: { ...scoringConfig }
    };
  }

  function computeComposite(highTempAcc, lowTempAcc, precipAcc, windAcc) {
    const ht = highTempAcc ?? 0;
    const lt = lowTempAcc ?? 0;
    const pr = precipAcc ?? 0;
    const wi = windAcc ?? 0;
    return scoringConfig.weightHighTemp * ht +
           scoringConfig.weightLowTemp * lt +
           scoringConfig.weightPrecip * pr +
           scoringConfig.weightWind * wi;
  }

  // =========================================================================
  // Helpers
  // =========================================================================

  function generateDefaultName() {
    return `ForecastAccuracy_${version.value}_${new Date().toISOString().slice(0, 10)}`;
  }

  // =========================================================================
  // Persistence (localStorage)
  // =========================================================================

  function saveConfig() {
    try {
      const config = {
        version: version.value,
        maxLeadDays: maxLeadDays.value,
        unitGroup: unitGroup.value,
        startDate: startDate.value,
        endDate: endDate.value,
        locations: locations.value,
        scoringConfig: { ...scoringConfig },
        dailyElements: dailyElements.value,
        hourlyElements: hourlyElements.value
      };
      localStorage.setItem(storageKey, JSON.stringify(config));
    } catch (e) { /* ignore */ }
  }

  function loadConfig() {
    try {
      const saved = localStorage.getItem(storageKey);
      if (!saved) return;
      const config = JSON.parse(saved);
      if (config.version) version.value = config.version;
      if (config.maxLeadDays) maxLeadDays.value = config.maxLeadDays;
      if (config.unitGroup) unitGroup.value = config.unitGroup;
      if (config.startDate) startDate.value = config.startDate;
      if (config.endDate) endDate.value = config.endDate;
      if (config.locations) locations.value = config.locations;
      if (config.dailyElements) dailyElements.value = config.dailyElements;
      if (config.hourlyElements) hourlyElements.value = config.hourlyElements;
      if (config.scoringConfig) {
        Object.assign(scoringConfig, config.scoringConfig);
      }
    } catch (e) { /* ignore */ }
  }

  // Load saved config on creation
  loadConfig();

  // =========================================================================
  // Return
  // =========================================================================

  return {
    // Session
    sessionState,
    isSessionReady,
    serverRoot,
    initSession,

    // Definition config
    version,
    maxLeadDays,
    unitGroup,
    startDate,
    endDate,
    dailyElements,
    hourlyElements,
    scoringConfig,
    definitionJson,

    // Locations
    locations,
    setLocations,
    addLocation,
    removeLocation,
    clearLocations,

    // Metadata & job state
    localMetadata,
    taskInstance,
    pollingState,

    // Save
    canRun,
    canSave,
    isSaving,
    saveError,
    saveDefnName,
    saveDefnDesc,
    pendingRunOnce,
    showConfirmOverwrite,
    saveAndRun,
    saveOnly,
    saveAsBulkTask,
    confirmOverwrite,
    cancelOverwrite,
    generateDefaultName,
    newDefinition,

    // Polling
    isRunning,
    pollingStatus,
    runError,
    stopPolling,
    setOnPollComplete: (fn) => { onPollComplete = fn; },

    // Results
    hasResults,
    hasResultError,
    hasResultComplete,
    results,
    summary,
    locationResults,
    definition,
    isLoadingResult,
    loadResults,
    clientScores,
    reEvaluateScores,

    // Saved definitions
    savedDefinitions,
    isLoadingDefinitions,
    loadSavedDefinitions,
    selectDefinition,
    deleteDefinition,

    // Downloads
    downloadResultJson,
    downloadResultCsv,

    // Config persistence
    saveConfig,
    loadConfig
  };
}
